import { useEffect, useMemo, type ReactElement } from 'react';
import { Box, Group, Loader, Stack, Text } from '@mantine/core';
import { Building2 } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import { useSurface } from '@/shared/surfaces/useSurface';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import type { StatItem } from '@/design/StatBand';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { formatCompact, formatCount, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import { TokenUsageCostDrilldownViewModel } from './TokenUsageCostDrilldownViewModel';
import TokenUsageCostRankedList, { type CostListRow } from './TokenUsageCostRankedList';
import TokenUsageCostByDepartmentInsights, { type DepartmentCostInsightRow } from './TokenUsageCostByDepartmentInsights';

const ACCENT = 'teal';

/**
 * Cost › By Department — the by-provider page's team-facing sibling. A ranked
 * list of departments by allocated spend fills the content column, each row
 * expanding in place to reveal its people (who drill into their prompt-level
 * evidence); a spend summary over AI insights sits in the right rail.
 */
export default function TokenUsageCostByDepartmentPage(): ReactElement {
    const surface = useSurface('token-usage');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <Body surface={surface} />
        </SurfaceAssistScope>
    );
}

function Body({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(TokenUsageCostDrilldownViewModel);
    const filters = useAssistFilters();
    const scopeKey = useMemo(() => JSON.stringify(filters), [filters]);

    useEffect(() => {
        void vm.loadTree(filters);
    }, [vm, filters]);

    const rows: CostListRow[] = vm.tree.map((dept) => ({
        id: `dept:${dept.value}`,
        label: dept.value,
        subline: `${dept.people.length} ${dept.people.length === 1 ? 'person' : 'people'}`,
        tokens: dept.tokens,
        costUsd: dept.costUsd,
        share: dept.share,
        secondaryValue: dept.sessions,
        secondaryLabel: 'sessions',
        children: dept.people.map((person) => ({
            id: `person:${dept.value}:${person.value}`,
            to: `/cost/by-department/spend-top-consumers/${encodeURIComponent(person.value)}`,
            label: humanUser(person.value),
            tokens: person.tokens,
            costUsd: person.costUsd,
            share: person.share,
            secondaryValue: person.sessions,
            secondaryLabel: 'sessions',
        })),
    }));

    const stats: StatItem[] = [
        { label: 'Departments', value: formatCount(vm.tree.length), hint: 'with spend' },
        { label: 'Total tokens', value: formatCompact(vm.totalTokens) },
        { label: 'Total cost', value: formatUsd(vm.totalCostUsd) },
    ];

    const insightRows: DepartmentCostInsightRow[] = vm.tree.map((dept) => ({ label: dept.value, costUsd: dept.costUsd, share: dept.share }));

    const rail = (
        <Stack gap="md">
            <TopicSummaryPanel stats={stats} />
            <TokenUsageCostByDepartmentInsights rows={insightRows} totalCostUsd={vm.totalCostUsd} scopeKey={scopeKey} />
        </Stack>
    );

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent
                icon={Building2}
                eyebrow="Cost"
                title="Cost by Department"
                subtitle="Allocated spend by team — the cost footprint of each department. Expand one to see the people behind its consumption."
            />

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {vm.loading && rows.length === 0 ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        Ranking departments by spend…
                    </Text>
                </Group>
            : rows.length === 0 ?
                <Text size="sm" c="dimmed">
                    No department spend to show for the current filters.
                </Text>
            :   <SideRailPageLayout rail={rail}>
                    <TokenUsageCostRankedList rows={rows} accent={ACCENT} primaryHeader="Department / person" noun="departments" />
                </SideRailPageLayout>
            }
        </Box>
    );
}
